import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusChip, LoadingBlock } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = [
    { key: "dirty", label: "Dirty" },
    { key: "cleaning", label: "Cleaning" },
    { key: "inspected", label: "Inspected" },
    { key: "clean", label: "Clean" },
];

const NEXT = { dirty: "cleaning", cleaning: "inspected", inspected: "clean" };

export default function Housekeeping() {
    const [tasks, setTasks] = useState(null);
    const [order, setOrder] = useState(null);
    const [optimizing, setOptimizing] = useState(false);

    const load = async () => {
        const { data } = await api.get("/housekeeping");
        setTasks(data);
    };
    useEffect(() => {
        load();
    }, []);

    const advance = async (t) => {
        const next = NEXT[t.status];
        if (!next) return;
        await api.patch(`/housekeeping/${t.id}`, { status: next });
        toast.success(`Room ${t.room_number} → ${next}`);
        load();
    };

    const optimize = async () => {
        setOptimizing(true);
        try {
            const { data } = await api.post("/housekeeping/optimize");
            setOrder(data);
            toast.success("AI route optimized");
        } catch {
            toast.error("Optimize failed");
        } finally {
            setOptimizing(false);
        }
    };

    if (!tasks) return <LoadingBlock rows={6} />;

    const grouped = {};
    COLUMNS.forEach((c) => (grouped[c.key] = []));
    tasks.forEach((t) => (grouped[t.status] = grouped[t.status] || []).push(t));

    return (
        <>
            <PageHeader
                title="Housekeeping Board"
                subtitle="Move rooms through the cleaning flow. Use AI to optimize the route."
                actions={
                    <Button onClick={optimize} disabled={optimizing} data-testid="housekeeping-ai-optimize">
                        <Sparkles className="w-4 h-4 mr-1" /> {optimizing ? "Optimizing…" : "AI Optimize order"}
                    </Button>
                }
            />

            {order && (
                <Card className="mb-5">
                    <CardContent className="py-4">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                            Optimized cleaning route
                        </div>
                        <div className="text-sm mb-2">{order.explanation}</div>
                        <div className="flex flex-wrap gap-1.5" data-testid="housekeeping-optimized-order">
                            {(order.order || []).map((o, i) => (
                                <span key={i} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-xs">
                                    <span className="font-semibold tabular">{i + 1}.</span> Room {o.room_number}
                                    <span className="text-muted-foreground"> — {o.reason}</span>
                                </span>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="housekeeping-kanban">
                {COLUMNS.map((c) => (
                    <div key={c.key} className="rounded-xl border border-border bg-card" data-testid={`hk-col-${c.key}`}>
                        <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                            <div className="text-sm font-medium capitalize">{c.label}</div>
                            <span className="text-xs text-muted-foreground tabular">{(grouped[c.key] || []).length}</span>
                        </div>
                        <div className="p-3 space-y-2 min-h-[60px]">
                            {(grouped[c.key] || []).map((t) => (
                                <div key={t.id} className="rounded-lg border border-border bg-background p-3" data-testid="hk-card">
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="font-medium text-sm">Room {t.room_number}</div>
                                        <StatusChip status={t.priority} label={t.priority} />
                                    </div>
                                    <div className="text-xs text-muted-foreground mb-2">
                                        Assignee: {t.assigned_to || "—"} · {t.notes || "No notes"}
                                    </div>
                                    {NEXT[t.status] && (
                                        <Button size="sm" variant="outline" onClick={() => advance(t)} data-testid="hk-advance">
                                            Move to {NEXT[t.status]}
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}
